import React, { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  passwordSignIn,
  confirmNewPasswordSignIn,
  startPasswordReset,
  completePasswordReset,
  getCurrentAuthProvider,
  signUp,
} from "../../services/Auth/auth";
import Shield from "../ThreatModeling/images/shield.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const LoginForm = ({ onSignInSuccess, initialMode }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isSupabaseAuth = getCurrentAuthProvider() === "supabase";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  
  const isSignUpDefault =
    initialMode === "signUp" ||
    location.pathname === "/signup" ||
    location.pathname === "/register" ||
    searchParams.get("mode") === "signup" ||
    searchParams.get("mode") === "signUp" ||
    searchParams.get("signup") === "true";

  const [formState, setFormState] = useState(isSignUpDefault ? "signUp" : "signIn");

  useEffect(() => {
    if (isSignUpDefault) {
      setFormState("signUp");
    } else if (initialMode === "signIn") {
      setFormState("signIn");
    }
  }, [initialMode, isSignUpDefault]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    isValid: false,
    requirements: {
      minLength: false,
      hasUpperCase: false,
      hasLowerCase: false,
      hasNumber: false,
      hasSpecialChar: false,
    },
  });

  const validatePassword = (password) => {
    const requirements = {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
    return {
      isValid: Object.values(requirements).every(Boolean),
      requirements,
    };
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setNewPassword(newPassword);
    setPasswordValidation(validatePassword(newPassword));
  };

  const handleSignUpPasswordChange = (e) => {
    const val = e.target.value;
    setPassword(val);
    setPasswordValidation(validatePassword(val));
  };

  const handleSignIn = async (e) => {
    setLoading(true);
    e.preventDefault();
    setError("");
    try {
      const { isSignedIn, nextStep } = await passwordSignIn({ username, password });
      if (nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        setFormState("newPassword");
      } else if (isSignedIn) {
        onSignInSuccess?.();
      }
    } catch (error) {
      setError(error.message || "Error signing in");
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    setLoading(true);
    e.preventDefault();
    setError("");
    try {
      const { isSignedIn } = await confirmNewPasswordSignIn({ newPassword });
      if (isSignedIn) {
        onSignInSuccess?.();
      }
    } catch (error) {
      setError(error.message || "Error setting new password");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    setLoading(true);
    e.preventDefault();
    setError("");
    try {
      await startPasswordReset({ username });
      setConfirmNewPassword("");
      setNewPassword("");
      setFormState("resetPassword");
    } catch (error) {
      setError(error.message || "Error initiating password reset");
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    setLoading(true);
    e.preventDefault();
    setError("");
    try {
      if (!passwordValidation.isValid) {
        setError("Password does not meet requirements");
        setLoading(false);
        return;
      }
      const result = await signUp({ email: username, password });
      if (result.emailConfirmationRequired) {
        setFormState("confirmEmail");
      } else {
        onSignInSuccess?.();
      }
    } catch (error) {
      setError(error.message || "Error creating account");
    } finally {
      setLoading(false);
    }
  };

  const renderSignIn = () => (
    <>
      <img src={Shield} alt="SBM ThreatForge logo" className="h-8 w-auto mb-6" />
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-2">Welcome back</h1>
      <p className="text-sm text-muted-foreground mb-6 max-w-sm">
        Sign in to access the threat modeling console. New users can create an account below.
      </p>
      {error && <div className="text-sm text-destructive mb-4">{error}</div>}
      <form onSubmit={handleSignIn} className="space-y-4">
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {isSupabaseAuth ? "Email" : "Username"}
          </label>
          <Input
            type={isSupabaseAuth ? "email" : "text"}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Password
          </label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        <div className="flex justify-between items-center text-sm text-muted-foreground">
          <a
            href="#"
            className="text-primary hover:underline"
            onClick={(e) => {
              e.preventDefault();
              setFormState("forgotPassword");
            }}
          >
            Forgot Password?
          </a>
        </div>
        <div className="pt-2">
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Signing In..." : "Sign In"}
          </Button>
        </div>
      </form>
      <div className="mt-8 pt-6 border-t text-center">
        <p className="text-sm text-muted-foreground">
          Don&apos;t have an account?{" "}
          <a
            href="#"
            className="text-primary hover:underline ml-1"
            onClick={(e) => {
              e.preventDefault();
              setFormState("signUp");
              setError("");
            }}
          >
            Sign Up
          </a>
        </p>
      </div>
    </>
  );

  const renderSignUp = () => (
    <>
      <img src={Shield} alt="SBM ThreatForge logo" className="h-8 w-auto mb-6" />
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-2">Create Account</h1>
      <p className="text-sm text-muted-foreground mb-6">Sign up to access the threat modeling console.</p>
      {error && <div className="text-sm text-destructive mb-4">{error}</div>}
      <form onSubmit={handleSignUp} className="space-y-4">
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Email
          </label>
          <Input
            type="email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Password
          </label>
          <Input type="password" value={password} onChange={handleSignUpPasswordChange} required />
        </div>
        <div className="flex flex-col gap-1 mt-2 pl-2">
          <div className={`text-xs flex items-center gap-2 ${passwordValidation.requirements.minLength ? "text-primary" : "text-muted-foreground"}`}>
            <span>{passwordValidation.requirements.minLength ? "✓" : "✗"}</span>
            At least 8 characters
          </div>
          <div className={`text-xs flex items-center gap-2 ${passwordValidation.requirements.hasUpperCase ? "text-primary" : "text-muted-foreground"}`}>
            <span>{passwordValidation.requirements.hasUpperCase ? "✓" : "✗"}</span>
            One uppercase letter
          </div>
          <div className={`text-xs flex items-center gap-2 ${passwordValidation.requirements.hasLowerCase ? "text-primary" : "text-muted-foreground"}`}>
            <span>{passwordValidation.requirements.hasLowerCase ? "✓" : "✗"}</span>
            One lowercase letter
          </div>
          <div className={`text-xs flex items-center gap-2 ${passwordValidation.requirements.hasNumber ? "text-primary" : "text-muted-foreground"}`}>
            <span>{passwordValidation.requirements.hasNumber ? "✓" : "✗"}</span>
            One number
          </div>
          <div className={`text-xs flex items-center gap-2 ${passwordValidation.requirements.hasSpecialChar ? "text-primary" : "text-muted-foreground"}`}>
            <span>{passwordValidation.requirements.hasSpecialChar ? "✓" : "✗"}</span>
            One special character
          </div>
        </div>
        <div className="pt-2">
          <Button type="submit" className="w-full" disabled={loading || !passwordValidation.isValid}>
            {loading ? "Creating Account..." : "Create Account"}
          </Button>
        </div>
      </form>
      <div className="mt-8 pt-6 border-t text-center">
        <p className="text-sm text-muted-foreground">
          Already have an account?{" "}
          <a
            href="#"
            className="text-primary hover:underline ml-1"
            onClick={(e) => {
              e.preventDefault();
              setFormState("signIn");
              setError("");
            }}
          >
            Sign In
          </a>
        </p>
      </div>
    </>
  );

  const renderConfirmEmail = () => (
    <>
      <img src={Shield} alt="SBM ThreatForge logo" className="h-8 w-auto mb-6" />
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-2">Check Your Email</h1>
      <p className="text-sm text-muted-foreground mb-6 text-center">
        We&apos;ve sent a confirmation link to <strong className="text-foreground font-medium">{username}</strong>.
        <br />
        Please check your inbox and click the link to verify your account.
      </p>
      <div className="pt-2">
        <Button variant="outline" className="w-full" onClick={() => setFormState("signIn")}>
          Back to Sign In
        </Button>
      </div>
    </>
  );

  const renderNewPassword = () => (
    <>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-6">Set New Password</h1>
      {error && <div className="text-sm text-destructive mb-4">{error}</div>}
      <form onSubmit={handleNewPassword} className="space-y-4">
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            New Password
          </label>
          <Input type="password" value={newPassword} onChange={handlePasswordChange} required />
        </div>
        <div className="pt-2">
          <Button type="submit" className="w-full" disabled={loading || !passwordValidation.isValid}>
            {loading ? "Setting Password..." : "Set New Password"}
          </Button>
        </div>
      </form>
    </>
  );

  const renderForgotPassword = () => (
    <>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-6">Reset Password</h1>
      {error && <div className="text-sm text-destructive mb-4">{error}</div>}
      <form onSubmit={handleForgotPassword} className="space-y-4">
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {isSupabaseAuth ? "Email" : "Username"}
          </label>
          <Input
            type={isSupabaseAuth ? "email" : "text"}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </div>
        <div className="pt-2">
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Sending..." : isSupabaseAuth ? "Send Reset Email" : "Send Reset Code"}
          </Button>
        </div>
      </form>
      <div className="mt-6 flex items-center justify-between text-sm text-muted-foreground">
        <a
          href="#"
          className="text-primary hover:underline"
          onClick={(e) => {
            e.preventDefault();
            setFormState("signIn");
            setError("");
          }}
        >
          &larr; Back to Sign In
        </a>
      </div>
    </>
  );

  const renderResetPassword = () => (
    <>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-6">Reset Password</h1>
      {error && <div className="text-sm text-destructive mb-4">{error}</div>}
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setError("");
          if (newPassword !== confirmNewPassword) {
            setError("Passwords do not match");
            return;
          }
          if (!passwordValidation.isValid) {
            setError("Password does not meet requirements");
            return;
          }
          try {
            await completePasswordReset({ username, confirmationCode, newPassword });
            setFormState("signIn");
          } catch (error) {
            setError(error.message || "Error resetting password");
          }
        }}
        className="space-y-4"
      >
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {isSupabaseAuth ? "Recovery Code" : "Confirmation Code"}
          </label>
          <Input
            type="text"
            value={confirmationCode}
            onChange={(e) => setConfirmationCode(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            New Password
          </label>
          <Input type="password" value={newPassword} onChange={handlePasswordChange} required />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Confirm New Password
          </label>
          <Input
            type="password"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            required
          />
        </div>
        <div className="pt-2">
          <Button
            type="submit"
            className="w-full"
            disabled={loading || !passwordValidation.isValid || !confirmationCode || newPassword !== confirmNewPassword}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </Button>
        </div>
      </form>
      <div className="mt-6 flex items-center justify-between text-sm text-muted-foreground">
        <a
          href="#"
          className="text-primary hover:underline"
          onClick={(e) => {
            e.preventDefault();
            setFormState("signIn");
            setError("");
          }}
        >
          &larr; Back to Sign In
        </a>
      </div>
    </>
  );

  const forms = {
    signIn: renderSignIn,
    signUp: renderSignUp,
    confirmEmail: renderConfirmEmail,
    newPassword: renderNewPassword,
    forgotPassword: renderForgotPassword,
    resetPassword: renderResetPassword,
  };

  return (
    <div className="w-full max-w-[420px] text-foreground mx-auto">
      <div className="transition-all duration-500 ease-out opacity-100 translate-y-0">
        {forms[formState] ? forms[formState]() : forms.signIn()}
      </div>
    </div>
  );
};

export default LoginForm;
